// Traductions du mode Lite : page « dashboard ».
export const fr = {
  title: "Tableau de bord",
  greeting: { night: "Bonne nuit", morning: "Bonjour", evening: "Bonsoir" },
  subtitle: (greeting: string, name: string, date: string) => (name ? `${greeting} ${name} · ${date}` : `${greeting} · ${date}`),
  emptyTitle: "Ton tableau de bord t'attend",
  emptyText: "Dès tes premiers trades, tu verras ici ton résultat, ta régularité, tes meilleurs horaires et l'état de tes comptes prop.",
  kpi: {
    net: "Résultat net",
    vsPrevious: "vs période précédente",
    vsPreviousShort: "vs précédente",
    winRate: "Trades gagnants",
    winsLosses: (w: number, l: number) => ({ w: `${w} G`, l: `${l} P` }),
    profitFactor: "Profit factor",
    pfExcellent: "Excellent",
    pfSolid: "Solide",
    pfFragile: "Positif, fragile",
    pfNegative: "Négatif",
    ratio: "Gain moyen / perte moyenne",
    gain: "Gain",
    loss: "Perte",
    expectancy: "Espérance par trade",
    avgDuration: (d: string) => `Durée moyenne ${d}`,
    winDays: (p: string) => `Jours gagnants ${p}`,
    maxDrawdown: "Drawdown max",
    peakToTrough: "Du plus haut au plus bas",
    current: "Actuel",
    pts: (v: string) => `${v} pts`,
  },
  curve: {
    equity: "Courbe de résultat",
    daily: "Résultat par jour",
    cumul: "Cumul",
    perDay: "Par jour",
    drawdown: "Drawdown",
  },
  calendar: { title: "Calendrier", subtitle: "Clique sur un jour pour voir son détail" },
  distribution: {
    title: "Distribution des trades",
    subtitle: "Nombre de trades par tranche de résultat ($)",
    below: (v: string) => `< ${v}`,
    above: (v: string) => `> ${v}`,
  },
  sides: {
    title: "Long ou short",
    winners: (p: string) => `${p} gagnants`,
    longsBetter: "Tes longs rapportent plus sur la période.",
    shortsBetter: "Tes shorts rapportent plus sur la période.",
  },
  weekday: { title: "Par jour de la semaine" },
  hours: { title: "Horaires", subtitle: "Résultat par jour et par heure d'entrée" },
  bySymbol: "Par symbole",
  byStrategy: "Par stratégie",
  noStrategyData: "Aucune stratégie sur la période",
  today: {
    title: "Aujourd'hui",
    seeDay: "Voir la journée",
    range: (from: string, to: string) => `${from} à ${to}`,
    noTrade: "Pas de trade",
  },
  prop: {
    title: "Comptes prop",
    subtitle: "Le plus à risque en premier",
    link: "Prop Firm Manager",
    empty: "Aucun compte prop actif",
  },
  behavior: {
    title: "Comportement",
    subtitle: "Résultat des trades par tag",
    costs: (tag: string, amount: string, n: number) => `« ${tag} » te coûte ${amount} sur ${n} trade${n > 1 ? "s" : ""}.`,
    empty: "Aucun tag sur la période",
  },
  recent: {
    title: "Derniers trades",
    openJournal: "Ouvrir le journal",
    date: "Date",
    symbol: "Symbole",
    side: "Sens",
    account: "Compte",
    strategy: "Stratégie",
    qty: "Qté",
    entry: "Entrée",
    exit: "Sortie",
    duration: "Durée",
    result: "Résultat",
  },
};

export const en: typeof fr = {
  title: "Dashboard",
  greeting: { night: "Good night", morning: "Hello", evening: "Good evening" },
  subtitle: (greeting: string, name: string, date: string) => (name ? `${greeting} ${name} · ${date}` : `${greeting} · ${date}`),
  emptyTitle: "Your dashboard is waiting",
  emptyText: "As soon as you log your first trades, you'll see your result, your consistency, your best hours and the state of your prop accounts here.",
  kpi: {
    net: "Net result",
    vsPrevious: "vs previous period",
    vsPreviousShort: "vs previous",
    winRate: "Winning trades",
    winsLosses: (w: number, l: number) => ({ w: `${w} W`, l: `${l} L` }),
    profitFactor: "Profit factor",
    pfExcellent: "Excellent",
    pfSolid: "Solid",
    pfFragile: "Positive, fragile",
    pfNegative: "Negative",
    ratio: "Avg win / avg loss",
    gain: "Win",
    loss: "Loss",
    expectancy: "Expectancy per trade",
    avgDuration: (d: string) => `Avg duration ${d}`,
    winDays: (p: string) => `Winning days ${p}`,
    maxDrawdown: "Max drawdown",
    peakToTrough: "From peak to trough",
    current: "Current",
    pts: (v: string) => `${v} pts`,
  },
  curve: {
    equity: "Equity curve",
    daily: "Daily result",
    cumul: "Cumulative",
    perDay: "Per day",
    drawdown: "Drawdown",
  },
  calendar: { title: "Calendar", subtitle: "Click a day to see its details" },
  distribution: {
    title: "Trade distribution",
    subtitle: "Number of trades per result bucket ($)",
    below: (v: string) => `< ${v}`,
    above: (v: string) => `> ${v}`,
  },
  sides: {
    title: "Long or short",
    winners: (p: string) => `${p} winners`,
    longsBetter: "Your longs earn more over the period.",
    shortsBetter: "Your shorts earn more over the period.",
  },
  weekday: { title: "By day of the week" },
  hours: { title: "Hours", subtitle: "Result by day and entry hour" },
  bySymbol: "By symbol",
  byStrategy: "By strategy",
  noStrategyData: "No strategy in this period",
  today: {
    title: "Today",
    seeDay: "See the day",
    range: (from: string, to: string) => `${from} to ${to}`,
    noTrade: "No trades",
  },
  prop: {
    title: "Prop accounts",
    subtitle: "Riskiest first",
    link: "Prop Firm Manager",
    empty: "No active prop account",
  },
  behavior: {
    title: "Behavior",
    subtitle: "Trade result by tag",
    costs: (tag: string, amount: string, n: number) => `"${tag}" costs you ${amount} over ${n} trade${n > 1 ? "s" : ""}.`,
    empty: "No tags in this period",
  },
  recent: {
    title: "Latest trades",
    openJournal: "Open the journal",
    date: "Date",
    symbol: "Symbol",
    side: "Side",
    account: "Account",
    strategy: "Strategy",
    qty: "Qty",
    entry: "Entry",
    exit: "Exit",
    duration: "Duration",
    result: "Result",
  },
};
